import {entries, objectFromEntries} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {addSearchResultsToFormats} from 'notebook/mediocre/formatted-text/FormattedTextSearchResults';
import FormattersConstants, {
  ExternalLink,
  Format,
  FormatColor,
  FormatData,
  FormatInternalLink,
  FormatLink,
  FormatPosition,
  InternalLink,
  SearchResultsFormatData,
} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {renderNumberedList} from 'notebook/mediocre/formatted-text/renderNumberedList';
import {getSelectedSearchResultDataAttrs} from 'notebook/mediocre/search/DOM';
import {
  EMPTY_SEARCH_RESULT_PROPS,
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {UseEntryChipStateInterface} from 'notebook/notes/core-ui/useEntryChipState';
import * as TableConstants from 'notebook/tables/constants/TableConstants';
import {COLORS} from 'ui-library/ColorPickerConstants';
import isValidLink from 'util/isValidLink';
import * as keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';

import parseDOMColor from '../clipboard/parseDOMColor';

interface Partition {
  before: string;
  after: string;
  selected: string;
}

const {
  BOLD,
  CYCLE_HEADER,
  LINK,
  INTERNAL_LINK,
  ITALIC,
  MONOSPACE,
  UNDERLINE,
  STRIKETHROUGH,
  SUBSCRIPT,
  SUPERSCRIPT,
  FOREGROUND_COLOR,
  BACKGROUND_COLOR,
  CHIP_WIDTH,
  SEARCH_RESULT,
} = FormattersConstants;

const FORMAT_TO_TAG: Record<string, string> = Object.fromEntries([
  [BOLD, 'B'],
  [ITALIC, 'I'],
  [UNDERLINE, 'U'],
  [STRIKETHROUGH, 'DEL'],
  [SUBSCRIPT, 'SUB'],
  [SUPERSCRIPT, 'SUP'],
  [LINK, 'A'],
  [FOREGROUND_COLOR, 'SPAN'],
  [BACKGROUND_COLOR, 'SPAN'],
  [SEARCH_RESULT, 'SPAN'],
  [MONOSPACE, 'CODE'],
]);

const FORMATS_WITHOUT_TAG_PARSE: Array<string> = [FOREGROUND_COLOR, BACKGROUND_COLOR, SEARCH_RESULT];

const TAG_TO_FORMAT: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(FORMAT_TO_TAG)
      .filter(([format]) => !FORMATS_WITHOUT_TAG_PARSE.includes(format))
      .map(([format, tag]) => [tag, format])
  ),
  STRONG: BOLD,
  EM: ITALIC,
};

interface FormatRange {
  start: number;
  end: number;
  data?: FormatData;
}

interface FormatEvent {
  format: string;
  index: number;
  type: number;
  data?: FormatData;
}

interface FormatDataEntry {
  format: keyof Format;
  data: FormatData;
}

interface RenderHTMLOptions extends MediocreSearchResultProps {
  processText?: (text: string) => string;
  paragraphMode?: boolean;
  forceNonEmpty?: boolean;
  listType?: string;
  indentation?: number;
  calculatedListIndex?: number | null;
  chipDisplayNameOverrides?: UseEntryChipStateInterface | null;
}

interface GetFormattedTextOptions {
  getNodeName?: (nodeName: string) => string;
}

const EVENT = {START: 0, END: 1, POINT: 2};
let HAS_WARNED_FOR_EMPTY_RANGE = false;

const SHORTCUT_TO_FORMAT: {[key: string]: keyof Format} = {
  'Ctrl + B': 'bold',
  'Ctrl + I': 'italic',
  'Ctrl + U': 'underline',
  'Ctrl + Shift + X': 'strikethrough',
  'Ctrl + K': 'link',
  'Ctrl + Shift + K': 'monospace',
  'Ctrl + .': 'superscript',
  'Ctrl + Shift + .': 'subscript',
  // On Windows, Ctrl + Shift + . comes across as Ctrl + Shift + >
  'Ctrl + Shift + >': 'subscript',
};

type ShortcutAction = 'symbol' | 'day';

const SHORTCUT_TO_ACTION: {[key: string]: ShortcutAction} = {
  'Ctrl + ;': 'symbol',
  'Ctrl + \\': 'day',
};

const FORMAT_TO_SHORTCUT: Record<string, string> = Object.fromEntries(
  Object.entries({...SHORTCUT_TO_FORMAT, ...SHORTCUT_TO_ACTION}).map(([shortcut, format]) => [
    format,
    shortcut,
  ])
);

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
};

function escapeHTML(value: string): string {
  return String(value).replace(/[&<>"'`]/g, (char) => HTML_ESCAPE_MAP[char]);
}

function uniqueBy<T, K>(array: Array<T>, keyFn: (item: T) => K): Array<T> {
  const seen = new Set<K>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

type ColorFormatKeys = 'foregroundColor' | 'backgroundColor';
type SubOrSuperscriptFormatKeys = 'subscript' | 'superscript';

/** Type Guards ************************ */
function isInternalLinkFormat(format: keyof Format): format is 'internalLink' {
  return format === 'internalLink';
}

function isFormatPosition(range: FormatInternalLink | FormatPosition): range is FormatPosition {
  return (range as FormatPosition).start != null && (range as FormatPosition).end != null;
}

function isFormatPointPosition(range: FormatInternalLink | FormatPosition): range is FormatInternalLink {
  return (range as FormatInternalLink).position != null;
}

function isColorFormat(format: keyof Format): format is ColorFormatKeys {
  return ['foregroundColor', 'backgroundColor'].includes(format);
}

function isSubOrSuperscriptFormat(format: keyof Format): format is SubOrSuperscriptFormatKeys {
  return ['subscript', 'superscript'].includes(format);
}

/** End Type Guards ************************ */

function sanitizeColorRanges<K extends 'foregroundColor' | 'backgroundColor'>(
  formatRanges: Format,
  key: K
): Format[K] {
  const ranges = formatRanges?.[key] ?? [];
  assert(
    ranges.every((range) => range.start != null && range.end != null),
    `Null type in data: ${JSON.stringify(ranges)}`
  );
  assert(
    ranges.every((r: FormatRange) => r.start <= r.end),
    `Invalid range values: ${JSON.stringify(ranges)}`
  );
  const filteredRanges = ranges
    .filter((range) => range.start < range.end)
    .map((range) => ({start: range.start, end: range.end, data: {color: range.data.color}}));
  return uniqueBy(filteredRanges, (range) => `${range.start}_${range.end}_${range.data.color}`).sort(
    (a, b) => a.start - b.start
  );
}

type NonColorFormatKey =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'subscript'
  | 'superscript'
  | 'monospace';

function sanitizeNonColorRanges(formatRanges: Format, key: NonColorFormatKey): Array<FormatPosition> {
  const ranges = (formatRanges?.[key] ?? []).filter(
    (range) => (range.start <= range.end && range.data == null) || isEqual(range.data, {})
  );
  assert(
    ranges.every((range) => range.start != null && range.end != null),
    `Null type in data: ${JSON.stringify(ranges)}`
  );
  assert(
    ranges.every((r: FormatRange) => r.start <= r.end),
    `Invalid range values: ${JSON.stringify(ranges)}`
  );
  assert(
    ranges.every((r: FormatRange) => r.data == null || isEqual(r.data, {})),
    'None of these formats have data attributes, since we throw them away.'
  );
  const flattened = ranges
    .filter((range) => range.start < range.end)
    .map((range) => [
      {type: EVENT.START, index: range.start, format: key},
      {type: EVENT.END, index: range.end, format: key},
    ])
    .flat()
    .sort((a, b) => a.index - b.index || a.type - b.type);

  const combined: Array<FormatPosition> = [];
  let numStarts = 0;
  let currentStart: number | null = null;
  for (const curr of flattened) {
    if (curr.type === EVENT.START) {
      if (numStarts === 0) {
        currentStart = curr.index;
      }
      numStarts += 1;
    } else {
      numStarts -= 1;
      if (numStarts === 0) {
        combined.push({start: currentStart!, end: curr.index, data: {}});
      }
    }
  }
  return combined;
}

function combineFormat<T extends keyof Format>(formatKey: T, destination: Format, source: Format): void {
  if (destination[formatKey] == null) {
    destination[formatKey] = source[formatKey];
  } else {
    // @ts-expect-error: Typescript doesn't realize that we're indexing from the same location we're inserting
    destination[formatKey].push(...(source[formatKey] ?? []));
  }
}

function typedKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

function combineAllFormats(destination: Format, source: Format): void {
  typedKeys(source).forEach((formatKey) => {
    combineFormat(formatKey, destination, source);
  });
}

// Sanitizes a dictionary of format range arrays. Merges ranges that overlap (except links),
// and removes empty ranges.
function processRanges(formatRanges: Format): Format {
  const newFormats: Partial<Format> = {};
  const colorFormatKeys: Array<'foregroundColor' | 'backgroundColor'> = [
    'foregroundColor',
    'backgroundColor',
  ];
  const formatKeys = [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'subscript',
    'superscript',
    'monospace',
  ] as const satisfies Array<keyof Format>;
  colorFormatKeys.forEach((colorFormatKey) => {
    newFormats[colorFormatKey] = sanitizeColorRanges(formatRanges, colorFormatKey);
  });
  formatKeys.forEach((formatKey) => {
    newFormats[formatKey] = sanitizeNonColorRanges(formatRanges, formatKey);
  });
  // Don't do any processing on links (don't combine contiguous ones, they should never overlap anyway).
  newFormats.internalLink = formatRanges?.internalLink ?? [];
  newFormats.link = formatRanges?.link ?? [];
  return newFormats as Format;
}

function hasPointFormatting(formats: Partial<Format> | null | undefined): boolean {
  return (formats?.internalLink?.length ?? 0) > 0;
}

function addRange(formatRanges: Format, format: string, range: FormatRange): Format {
  const newFormats = reactUpdate(formatRanges, benchlingUtil.kv(format, {$push: [range]}));
  return processRanges(newFormats);
}

function removeRange(
  formatRanges: Format,
  format: keyof Format,
  rangeToRemove: {start: number; end: number}
): Format {
  if (format === 'internalLink') {
    const ranges = formatRanges[format];
    const newRanges: Array<FormatInternalLink> = [];
    for (const range of Array.from(ranges)) {
      if (range.position < rangeToRemove.start || range.position > rangeToRemove.end) {
        newRanges.push({position: range.position, data: range.data});
      }
    }
    const merge = {internalLink: newRanges} satisfies Partial<Format>;
    const newFormats = reactUpdate(formatRanges, {$merge: merge});
    return processRanges(newFormats);
  }
  const ranges = (formatRanges[format] ?? []) as Array<FormatRange>;
  const newRanges: Array<FormatRange> = [];
  for (const range of Array.from(ranges)) {
    const overlapStart = Math.max(range.start, rangeToRemove.start);
    const overlapEnd = Math.min(range.end, rangeToRemove.end);
    if (overlapStart >= overlapEnd) {
      newRanges.push(range);
    } else {
      // Adds the outer parts of the range that are not in overlap. These may be empty,
      // and are cleaned up in processRanges.
      newRanges.push({...range, start: range.start, end: overlapStart});
      newRanges.push({...range, start: overlapEnd, end: range.end});
    }
  }
  const merge = {[format]: newRanges} as Partial<Format>;
  const newFormats = reactUpdate(formatRanges, {$merge: merge});
  return processRanges(newFormats);
}

// Given an object of format ranges, returns an array of event points (starts or ends) that
// correspond to a valid DOM representation of the formats. For instance, <b><i></i></b> is valid
// but <b></i></b></i> is not.
// Links are handled specially, since they can't be started and stopped arbitrarily (i.e <a>hi</a><a>hi</a>
//   is different from <a>hihi</a>).
// We go through a sorted array of events, and keep a stack of 'open' formats. Whenever we reach a 'close'
// format, we close events on the stack until we reach the current format. Then, we add the previous
// formats back onto the stack.
// This results in {bold: [0,5], italic: [2:7]} looking like <b>AA<i>BBB</i></b><i>CC</i>.
// Some formats are located at a point rather than a range. When a point format is reached all currently
// open formats are closed before adding the point onto the linearized list. The closed formats are reopened
// by adding them back onto the stack.
// oxlint-disable-next-line typescript/no-explicit-any
function linearizeFormats(formats: Format): Array<FormatEvent> {
  // Given the current format point, close everything on the stack until we find the given format. Add that
  // format onto the returned events array, then put everything back on the stack. Usually, this is used for
  // close events, but this is also used for open LINK events (since we need to close everything before
  // starting a link).

  function addToOutput(linearized: Array<FormatEvent>, point: FormatEvent): void {
    if (linearized.length === 0) {
      linearized.push(point);
      return;
    }

    const last = linearized[linearized.length - 1];
    const {type, format, index} = point;
    if (type === EVENT.END && last.type === EVENT.START && format === last.format && index === last.index) {
      linearized.pop();
    } else {
      linearized.push(point);
    }
  }

  function clearStackFor(
    linearized: Array<FormatEvent>,
    stack: Array<FormatEvent>,
    newFormatPoint: FormatEvent
  ): void {
    let formatPoint: FormatEvent | undefined;
    const splits: Array<FormatEvent> = [];
    while (stack.length > 0) {
      formatPoint = stack.pop();
      if (formatPoint == null || formatPoint.format === newFormatPoint.format) {
        break;
      }
      addToOutput(linearized, {format: formatPoint.format, index: newFormatPoint.index, type: EVENT.END});
      splits.push(formatPoint);
    }

    addToOutput(linearized, newFormatPoint);
    for (let i = splits.length - 1; i >= 0; i--) {
      const point = splits[i];
      const newStart = {
        format: point.format,
        data: point.data,
        index: newFormatPoint.index,
        type: EVENT.START,
      };
      stack.push(newStart);
      addToOutput(linearized, newStart);
    }
  }

  const combinedFormats: Array<FormatEvent> = [];
  typedKeys(formats).forEach((format) => {
    const ranges = formats[format];
    for (const range of ranges || []) {
      if (isInternalLinkFormat(format)) {
        combinedFormats.push({
          format,
          data: range.data,
          index: (range as FormatInternalLink).position,
          type: EVENT.POINT,
        });
      } else {
        // If a format has 0 length (which it never should!) we should skip over it and log it.
        if ((range as FormatRange).start === (range as FormatRange).end) {
          // We should only error for this once.
          if (!HAS_WARNED_FOR_EMPTY_RANGE) {
            // Updated message to include the task number (T5755) at the end so we can distinguish
            // between errors that occurred before the getFormattedTextFromRenderedElement fix and after.
            logging.warn(`Attempted to add a range with 0 length for format ${format} (T5755)`);
            HAS_WARNED_FOR_EMPTY_RANGE = true;
          }
          continue;
        }
        combinedFormats.push({
          format,
          data: (range as FormatRange).data,
          index: (range as FormatRange).start,
          type: EVENT.START,
        });
        combinedFormats.push({format, index: (range as FormatRange).end, type: EVENT.END});
      }
    }
  });

  function sortOrder({type}: {type: number}): number {
    switch (type) {
      case EVENT.END:
        return 0; // ranges must terminate before another or a point
      case EVENT.POINT:
        return 1; // points take precedence over start
      case EVENT.START:
        return 2;
      default:
        return 3;
    }
  }

  combinedFormats.sort((a, b) => a.index - b.index || sortOrder(a) - sortOrder(b));
  const stack: Array<FormatEvent> = [];
  const linearized: Array<FormatEvent> = [];
  for (const formatPoint of Array.from(combinedFormats)) {
    if (formatPoint.type === EVENT.START) {
      // We never split
      // - LINK: a hyperlink's hover state / interaction would also be split.
      // - MONOSPACE: it has padding + backgroundColor which will clash with a wrapping BACKGORUND_COLOR
      if (formatPoint.format === LINK || formatPoint.format === MONOSPACE) {
        clearStackFor(linearized, stack, formatPoint);
      } else {
        addToOutput(linearized, formatPoint);
        stack.push(formatPoint);
      }
    } else {
      clearStackFor(linearized, stack, formatPoint);
    }
  }

  return linearized;
}

function renderAttrs(attributes: {[key: string]: string}): string {
  return Object.entries(attributes)
    .map(([key, value]) => `${key}='${escapeHTML(value)}'`)
    .join(' ');
}

function renderFormatStart(format: string, data: FormatData | SearchResultsFormatData | undefined): string {
  const tag = FORMAT_TO_TAG[format];
  if (format === LINK) {
    const formatData = data as FormatData | undefined;
    let url = escapeHTML(formatData?.url ?? '');
    if (formatData?.url && formatData?.url.indexOf(':') === -1) {
      url = `http://${url}`;
    }
    return `<A href='${url}' rel='nofollow noopener noreferrer' target='_blank'>`;
  } else if (format === SEARCH_RESULT) {
    // TODO(BNCH-40448): Selecting search result text, NoteToolbar will surface as a background color.
    return `<${tag} ${renderAttrs(
      getSelectedSearchResultDataAttrs(
        (data as SearchResultsFormatData).selected ? (data as SearchResultsFormatData).uuid : undefined
      )
    )} class='mediocre-searchResult ${(data as SearchResultsFormatData).selected ? 'is-selected' : ''}' >`;
  } else if (format === FOREGROUND_COLOR || format === BACKGROUND_COLOR) {
    // It's non-trivial to escape user input for CSS, so we only include it we detect a valid CSS color.
    // `color` will be null or a hex value (e.g. #FF0000)
    const color = parseDOMColor((data as FormatColor['data']).color);
    const attr = format === FOREGROUND_COLOR ? 'color' : 'background-color';
    if (color) {
      return `<${tag} style='${escapeHTML(attr)}:${escapeHTML(color)}'>`;
    }
  }
  return `<${tag}>`;
}

function renderFormatEnd(format: string): string {
  return `</${FORMAT_TO_TAG[format]}>`;
}

function renderList(data: string, indentation: number): string {
  return `${'<UL>'.repeat(indentation)}<LI style='margin:0'>${data}</LI>${'</UL>'.repeat(indentation)}`;
}

function renderFormatPoint(format: string, data: FormatData | undefined, param: RenderHTMLOptions): string {
  assert(format === INTERNAL_LINK, 'renderFormatPoint: format must be INTERNAL_LINK');
  const InternalLinkComponent = require('notebook/mediocre/mentions/InternalLink').default; // Avoid circular deps

  const chipOverride = data && data.id && param.chipDisplayNameOverrides?.[data.id];

  return ReactDOMServer.renderToStaticMarkup(
    <InternalLinkComponent
      internalLink={data}
      chipOverride={chipOverride}
      {...forwardSearchResultProps(param)}
    />
  );
}

function getFormatData(el: HTMLElement, options?: GetFormattedTextOptions): Array<FormatDataEntry> {
  if (options == null) {
    options = {};
  }

  const formats: Array<FormatDataEntry> = [];

  // Abort for non-element nodes (e.g. text nodes). There are no styles / attributes to parse otherwise.
  if (el.nodeType !== Node.ELEMENT_NODE) {
    return formats;
  }

  let nodeName = typeof options.getNodeName === 'function' ? options.getNodeName(el.nodeName) : undefined;
  nodeName = nodeName != null ? nodeName : el.nodeName;

  // Handle internalLinks first as we don't actually want to process their internal styles.
  if (nodeName === 'SPAN' && $(el).hasClass('internalLink')) {
    const internalLink = JSON.parse(el.getAttribute('data-internal-link')!);
    formats.push({format: INTERNAL_LINK as keyof Format, data: internalLink});
    return formats;
  }

  // Read format from nodeName
  const format = TAG_TO_FORMAT[nodeName] as keyof Format;
  if (format === LINK) {
    const url = el.getAttribute('href') ?? undefined;
    if (isValidLink(url)) {
      formats.push({format: LINK, data: {url}});
    }
  } else if (format != null && format !== FOREGROUND_COLOR && format !== BACKGROUND_COLOR) {
    formats.push({format, data: {}});
  }

  // If style is undefined, return.
  // Note that el.style is only undefined for non-HTML attributes (important for imports).
  if (el.style == null) {
    return formats;
  }

  // Read format from styles
  if (el.style.fontWeight === 'bold') {
    formats.push({format: BOLD as keyof Format, data: {}});
  }
  if (el.style.textDecoration === 'underline') {
    formats.push({format: UNDERLINE as keyof Format, data: {}});
  }
  if (el.style.fontStyle === 'italic') {
    formats.push({format: ITALIC as keyof Format, data: {}});
  }
  if (el.style.textDecoration === 'line-through' || ['DEL', 'STRIKE'].includes(nodeName)) {
    formats.push({format: STRIKETHROUGH as keyof Format, data: {}});
  }
  if (el.style.fontFamily && el.style.fontFamily.match(/\b(mono|monospace|courier)\b/i)) {
    formats.push({format: MONOSPACE as keyof Format, data: {}});
  }
  const foregroundColor = parseDOMColor(el.style.color || el.getAttribute('color'));
  if (foregroundColor != null) {
    formats.push({
      format: FOREGROUND_COLOR as keyof Format,
      data: {color: foregroundColor},
    });
  }
  const backgroundColor = parseDOMColor(el.style.backgroundColor);
  if (backgroundColor != null) {
    formats.push({
      format: BACKGROUND_COLOR as keyof Format,
      data: {color: backgroundColor},
    });
  }

  // [BNCH-6031] OneNote uses style='vertical-align:super' for superscripts
  if (el.style.verticalAlign === 'super') {
    formats.push({
      format: SUPERSCRIPT as keyof Format,
      data: {},
    });
  }

  return formats;
}

function getOverlappingLinks(linkRanges: Array<FormatLink>, start: number, end: number): Array<FormatLink> {
  return linkRanges.filter((range) => {
    if (start === end) {
      return range.start <= start && start < range.end;
    }
    return Math.max(start, range.start) < Math.min(end, range.end);
  });
}

const formatters = {
  _testing: {
    processRanges,
    addRange,
    linearizeFormats,
    renderFormatStart,
    renderFormatEnd,
    getFormatData,
    getOverlappingLinks,
    renderAttrs,
  },

  defaultFormats(): Format {
    return {
      bold: [],
      italic: [],
      underline: [],
      strikethrough: [],
      subscript: [],
      superscript: [],
      link: [],
      internalLink: [],
      foregroundColor: [],
      backgroundColor: [],
      monospace: [],
    };
  },

  formatTypeFromKeydown(e: keyboard.AnyKeyboardEvent, param?: {allowLink?: boolean}): string | null {
    if (param == null) {
      param = {};
    }
    let {allowLink} = param;
    if (allowLink == null) {
      allowLink = true;
    }
    // CYCLE_HEADER is a special shortcut in that we use real Ctrl and not Cmd on Macs
    if (e.key === '`' && e.ctrlKey && !e.shiftKey && !e.metaKey && !e.altKey) {
      return CYCLE_HEADER;
    }
    const shortcut = keyboard.getNormalizedShortcut(e);
    const format: keyof Format | ShortcutAction | null = Object.hasOwn(SHORTCUT_TO_FORMAT, shortcut)
      ? SHORTCUT_TO_FORMAT[shortcut]
      : Object.hasOwn(SHORTCUT_TO_ACTION, shortcut)
        ? SHORTCUT_TO_ACTION[shortcut]
        : null;
    if (format === 'link' && !allowLink) {
      return null;
    }
    if (format === 'day' && BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS) {
      return FormattersConstants.SECTION;
    }
    return format;
  },

  /*
  Takes an array of formatted texts ({text, formats}) and applies the given format, toggling on if any
  text is not fully-formatted or toggling off if all texts are fully formatted.
  */
  formatTexts(
    fmtTexts: Array<{text: string; formats: Format}>,
    {format, data}: {format: keyof Format; data?: FormatData}
  ): Array<{text: string; formats: Format}> {
    assert(format !== LINK, 'formatTexts does not support LINK format');
    // Toggle format on if there is any item that is not entirely formatted.
    const removeFmt = fmtTexts.every(({text, formats}) => {
      if (text.length === 0) {
        return true;
      }
      const ranges = formats[format] ?? [];
      return (
        ranges.length === 1 &&
        (ranges[0] as FormatRange).start === 0 &&
        (ranges[0] as FormatRange).end === text.length
      );
    });

    // Apply the formatting
    return fmtTexts.map(({text, formats}) => {
      const partition = {before: '', selected: text, after: ''};
      ({text, formats} = formatters.formatText(formats, {
        format,
        data,
        partition,
        disableToggle: !removeFmt,
      }));
      return {text, formats};
    });
  },

  /*
  Takes a format (bold, italics, underline, subscript, superscript) and a selection partition,
  and returns the post-formatted text and selection start/end. Links are handled separately.
  */
  formatText(
    currentFormatRanges: Format,
    {
      format,
      data,
      partition,
      disableToggle,
    }: {format: keyof Format; data?: FormatData; partition: Partition; disableToggle?: boolean}
  ): {formats: Format; start: number; end: number; text: string} {
    let newFormats;
    assert(format !== LINK, 'formatPartition does not support LINK format');
    if (format === FOREGROUND_COLOR || format === BACKGROUND_COLOR) {
      disableToggle = true;
    }
    if (disableToggle == null) {
      disableToggle = false;
    } // disallow toggling if the range is fully formatted.
    const {before, selected, after} = partition;
    const start = before.length;
    const end = start + selected.length;
    const oldRanges: Array<FormatInternalLink> | Array<FormatPosition> = currentFormatRanges[format] ?? [];

    // If the given range is already fully bolded, then unbold it. Otherwise bold it.
    if (
      !disableToggle &&
      oldRanges.some(
        (range: FormatInternalLink | FormatPosition) =>
          isFormatPosition(range) && range.start <= start && range.end >= end
      )
    ) {
      newFormats = removeRange(currentFormatRanges, format, {start, end});
    } else if (isColorFormat(format) && data?.color === COLORS.NO_STYLE) {
      newFormats = removeRange(currentFormatRanges, format, {start, end});
    } else {
      if (isSubOrSuperscriptFormat(format)) {
        // Right now our model doesn't support nested sub/superscripts, so when we use
        // one of them to format text, we need to clear the other for the full range.
        const otherFormat = format === 'subscript' ? 'superscript' : 'subscript';
        newFormats = removeRange(currentFormatRanges, otherFormat, {start, end});
      } else if (isColorFormat(format)) {
        newFormats = removeRange(currentFormatRanges, format, {start, end});
      } else {
        newFormats = currentFormatRanges;
      }
      newFormats = addRange(newFormats, format, {start, end, data});
    }
    return {formats: newFormats, start, end, text: before + selected + after};
  },

  /*
  Replaces the selected text with insertText (handling formats shifting)
  and returns the new formats, text, and the start and end of the newly inserted text.
  */
  replaceText(
    formats: Format,
    partition: Partition,
    replacementText: string
  ): {formats: Format; text: string; start: number; end: number} {
    const {before, after, selected} = partition;

    // Compute final text + selection
    const start = before.length;
    const end = start + replacementText.length;
    const text = before + replacementText + after;

    // If the text hasn't changed, the format is valid as is.
    if (replacementText === selected) {
      return {formats, text, start, end};
    }

    // Otherwise... first isolate the formats of the text being replaced into middleFormats
    const {before: beforeFormats, after: middleAfterFormats} = this.splitFormats(formats, before.length);
    const splitMiddleAfterFormats = this.splitFormats(middleAfterFormats, selected.length);
    let {before: middleFormats} = splitMiddleAfterFormats;
    const {after: afterFormats} = splitMiddleAfterFormats;

    // If the replace region is non-empty, use the formatting of the first character, otherwise no formatting.
    if (selected.length === 0 && before.length === 0) {
      middleFormats = this.defaultFormats();
    } else {
      if (selected.length > 0) {
        ({before: middleFormats} = this.splitFormats(middleFormats, 1));
      } else {
        // Set afterPoint to true since we do not want to keep the point formatting of the previous character.
        ({after: middleFormats} = this.splitFormats(beforeFormats, before.length - 1, true));
      }
      middleFormats = objectFromEntries(
        entries(middleFormats).map(([format, ranges]) => [
          format,
          ranges.length === 0 ? ranges : reactUpdate(ranges, {0: {end: {$set: replacementText.length}}}),
        ])
      ) as unknown as Format;
    }

    // Re-join the formats together with the new middleFormats for replacementText.
    const newFormats = this.joinFormats(
      this.joinFormats(beforeFormats, middleFormats, start),
      afterFormats,
      end
    );

    return {formats: newFormats, text, start, end};
  },

  replaceWithLink(
    formats: Format,
    partition: Partition,
    link: ExternalLink | null
  ): {formats: Format; text: string; start: number; end: number} {
    if (link == null) {
      return this.replaceText(formats, partition, partition.selected);
    }
    const replacedText = this.replaceText(formats, partition, link.text ?? partition.selected);
    ({formats} = replacedText);
    const {text, start, end} = replacedText;
    formats =
      link.url != null
        ? this.addLink(formats, {start, end, url: link.url})
        : this.removeLink(formats, {start, end});
    return {formats, text, start, end};
  },

  replaceWithInternalLink(
    formats: Format,
    partition: Partition,
    internalLink: InternalLink,
    index?: number
  ): {formats: Format; text: string; end: number} {
    const position = partition.before.length;
    const replacedText = this.replaceText(formats, partition, ' ');
    ({formats} = replacedText);
    const {text, end} = replacedText;
    formats = this.addInternalLink(formats, {position, internalLink}, index);
    return {formats, text, end};
  },

  addLink(currentFormatRanges: Format, {start, end, url}: {start: number; end: number; url: string}): Format {
    if (!isValidLink(url)) {
      return currentFormatRanges;
    }
    let links = currentFormatRanges.link;

    // Remove overlapping links
    const overlaps = getOverlappingLinks(currentFormatRanges.link, start, end);
    // eslint-disable-next-line no-restricted-syntax
    links = links.filter((link) => !overlaps.includes(link));

    // Add new link
    links = links.concat([{start, end, data: {url}}]);
    const merge = {link: links} satisfies Partial<Format>;
    const newFormats = reactUpdate(currentFormatRanges, {$merge: merge});
    return newFormats;
  },

  addInternalLink(
    currentFormatRanges: Format,
    {position, internalLink}: {position: number; internalLink: InternalLink},
    index?: number
  ): Format {
    const internalLinks = currentFormatRanges.internalLink.slice();
    internalLinks.splice(index ?? internalLinks.length, 0, {
      position,
      data: internalLink,
    });
    const merge = {internalLink: internalLinks} satisfies Partial<Format>;
    const newFormats = reactUpdate(currentFormatRanges, {$merge: merge});
    return newFormats;
  },

  removeLink(currentFormatRanges: Format, {start, end}: {start: number; end: number}): Format {
    let links = currentFormatRanges.link;
    // eslint-disable-next-line no-restricted-syntax
    links = links.filter((link) => !(link.start === start && link.end === end));
    const merge = {link: links} satisfies Partial<Format>;
    const newFormats = reactUpdate(currentFormatRanges, {$merge: merge});
    return newFormats;
  },

  // Returns the updated selection (if a link is partially selected), and the url
  // corresponding to the selected link. Also returns the before/selected/after text of the selection.
  getSelectionForAddLink(
    currentFormatRanges: Format,
    {partition}: {partition: Partition}
  ): Partition & {url: string} {
    let {before, after, selected} = partition;
    let start = before.length;
    let end = start + selected.length;
    const overlaps = getOverlappingLinks(currentFormatRanges.link, start, end);

    if (overlaps.length > 0) {
      start = Math.min(start, ...overlaps.map((overlap) => overlap.start));
      end = Math.max(end, ...overlaps.map((overlap) => overlap.end));
    }

    const fullText = before + selected + after;
    before = fullText.slice(0, start);
    after = fullText.slice(end);
    selected = fullText.slice(start, end);
    const url = overlaps.length > 0 ? (overlaps[0].data.url ?? '') : '';
    return {before, selected, after, url};
  },

  // Returns the HTML string for the given text/formats
  renderHTML(text: string, formats: Format, param?: Partial<RenderHTMLOptions>): string {
    // TODO(T1075) Return a react component instead of HTML string.
    const defaultParam: RenderHTMLOptions = {
      searchResultsTree: null,
      selectedSearchResultTree: null,
      ...(param ?? EMPTY_SEARCH_RESULT_PROPS),
    };
    const {processText, paragraphMode, forceNonEmpty, listType, indentation, calculatedListIndex} =
      defaultParam;
    formats = processRanges(formats); // TODO(T6442): this is to sanitize bad server data
    formats = addSearchResultsToFormats(formats, defaultParam);

    let html = '';
    let subtext;
    const linearFormats = linearizeFormats(formats);
    let curTextIndex = 0;
    for (const {format, index, type, data} of Array.from(linearFormats)) {
      subtext = escapeHTML(text.slice(curTextIndex, index));
      html += processText != null ? processText(subtext) : subtext;
      curTextIndex = index;
      if (type === EVENT.POINT) {
        html += renderFormatPoint(format, data, defaultParam);
      } else if (type === EVENT.START) {
        html += renderFormatStart(format, data);
      } else {
        assert(type === EVENT.END, 'renderHTML: type must be EVENT.END');
        html += renderFormatEnd(format);
      }
    }
    subtext = escapeHTML(text.slice(curTextIndex));
    if (paragraphMode) {
      html = `<P>${html.replace(/\n/g, '<P>')}`;
    }
    html += processText != null ? processText(subtext) : subtext;
    if (forceNonEmpty && html.length === 0) {
      return '&nbsp;';
    }
    // External editors (Word, Google Docs, etc.) do not respect our span/div
    // styling for indentation. Instead, wrap the line in nested list tags so
    // that the indentation and numbering survive paste.
    if (indentation) {
      if (listType === 'number') {
        html = renderNumberedList(html, indentation, calculatedListIndex);
      } else if (listType === 'bullet' || (typeof listType === 'string' && listType.includes('checkbox'))) {
        html = renderList(html, indentation);
      }
    }
    return html;
  },

  isUnformatted(formats: Format): boolean {
    return Object.entries(formats).every(([, ranges]) => ranges.length === 0);
  },

  // Returns whether formats contain any internal links, which render as point-format chips.
  hasPointFormatting,

  // Returns whether formatted text is empty = has no raw text and no point elements
  isEmpty(fmtTexts: Array<{text: string | TableConstants.Table; formats: Format}>): boolean {
    if (fmtTexts.length > 1) {
      return false;
    }
    if (fmtTexts.length === 0) {
      return true;
    }
    const {text, formats} = fmtTexts[0];
    const hasPointFormats = hasPointFormatting(formats);
    // Tables are always considered non-empty
    if (typeof text !== 'string') {
      return false;
    }
    return !(text.length || hasPointFormats);
  },

  // Returns the formats object starting from a given DOM node.
  // We currently only handle one level of super/subscript so any others will get flattened.
  getFormattedTextFromRenderedElement(
    el: HTMLElement | Node,
    offset: number = 0,
    isInSubSuperscript: boolean = false,
    shouldProcessRanges: boolean = true,
    options: GetFormattedTextOptions = {}
  ): {text: string; formats: Format} {
    let text = '';
    let formats = this.defaultFormats();
    if (el.nodeType === Node.TEXT_NODE) {
      return {text: el.textContent ?? '', formats};
    }
    let shouldRecur = true;
    for (const {format, data} of Array.from(getFormatData(el as HTMLElement, options))) {
      let shouldIncludeFormat = true;
      if ([SUPERSCRIPT, SUBSCRIPT].includes(format)) {
        shouldIncludeFormat = !isInSubSuperscript;
        isInSubSuperscript = true;
      }
      if (isInternalLinkFormat(format)) {
        shouldRecur = false;
        shouldIncludeFormat = false;
        formats.internalLink.push({position: offset, data: data as InternalLink});
      }
      if (shouldIncludeFormat) {
        // Only push a format if the text it formats is non-empty.
        const elText = $(el).text();
        if (elText.length !== 0) {
          (formats[format] as Array<FormatRange>).push({
            start: offset,
            end: offset + elText.length,
            data,
          });
        }
      }
    }

    if (shouldRecur) {
      $(el)
        .contents()
        .each((i: number, child: Node) => {
          const subFormattedText = this.getFormattedTextFromRenderedElement(
            child,
            offset,
            isInSubSuperscript,
            false, // `shouldProcessRanges` should be false on all recursive calls.
            options
          );
          const {text: subText, formats: subFormats} = subFormattedText;
          combineAllFormats(formats, subFormats);
          text += subText;
          offset += subText.length;
        });
    }

    // Only call `processRanges` on the outermost recursive call.
    if (shouldProcessRanges) {
      formats = processRanges(formats);
    }
    return {text, formats};
  },

  getSelectionIndicesFromFormatIndices(
    formats: Format,
    prevStart: number,
    prevEnd: number
  ): {start: number; end: number} {
    let start = prevStart;
    let end = prevEnd;
    for (const point of formats.internalLink) {
      if (point.position <= start) {
        start += CHIP_WIDTH;
      }
      if (point.position <= end) {
        end += CHIP_WIDTH;
      }
    }

    return {start, end};
  },

  // Splits a formats object at the given index, and returns the two format objects.
  splitFormats(formats: Format | null, index: number, afterPoint?: boolean): {before: Format; after: Format} {
    let before = this.defaultFormats();
    let after = this.defaultFormats();
    entries(formats ?? {}).forEach(
      ([format, ranges]: [keyof Format, Array<FormatPosition | FormatInternalLink>]) => {
        for (const range of Array.from(ranges)) {
          // Point formats have a position, not a start or end
          if (isFormatPointPosition(range)) {
            const pointRange = range;
            if (pointRange.position < index || (afterPoint && pointRange.position === index)) {
              before[format as 'internalLink'].push(pointRange);
            } else if (pointRange.position > index || (!afterPoint && pointRange.position === index)) {
              after[format as 'internalLink'].push({
                data: pointRange.data,
                position: pointRange.position - index,
              });
            }
          } else {
            const formatRange = range;
            if (formatRange.start < index) {
              (before[format] as Array<FormatRange>).push({
                start: formatRange.start,
                end: Math.min(index, formatRange.end),
                data: formatRange.data,
              });
            }
            if (formatRange.end > index) {
              const start = Math.max(formatRange.start, index) - index;
              const end = formatRange.end - index;
              (after[format] as Array<FormatRange>).push({
                start,
                end,
                data: formatRange.data,
              });
            }
          }
        }
      }
    );
    before = processRanges(before);
    after = processRanges(after);
    return {before, after};
  },

  // Joins two format objects into one, starting the second one at rightOffset.
  joinFormats(left: Format, right: Format, rightOffset: number): Format {
    const combined = this.defaultFormats();
    combineAllFormats(combined, left);
    typedKeys(right).forEach((format) => {
      (right[format] || []).forEach((range) => {
        // Point formats specify a position rather than a start and end
        if (isFormatPointPosition(range)) {
          const pointRange = range;
          combined[format]!.push({
            data: pointRange.data,
            position: pointRange.position + rightOffset,
          } as FormatPosition & FormatInternalLink & FormatColor & FormatLink);
        } else {
          const formatRange = range;
          assert(
            formatRange.start != null && formatRange.end != null,
            'joinFormats: formatRange must have start and end'
          );
          combined[format]!.push({
            start: formatRange.start + rightOffset,
            end: formatRange.end + rightOffset,
            data: formatRange.data,
          } as FormatPosition & FormatInternalLink & FormatColor & FormatLink);
        }
      });
    });
    return processRanges(combined);
  },

  trimLeft(text: string, formats: Format): {text: string; formats: Format} {
    const isLeftLink = formats.internalLink.some((data) => data.position === 0);
    const trimmedText = isLeftLink ? text : text.trimLeft();
    formats = this.splitFormats(formats, text.length - trimmedText.length, false).after;
    return {text: trimmedText, formats};
  },

  trimRight(text: string, formats: Format): {text: string; formats: Format} {
    const isRightLink = formats.internalLink.some((data) => data.position === text.length);
    const trimmedText = isRightLink ? text : text.trimRight();
    formats = this.splitFormats(formats, trimmedText.length, true).before;
    return {text: trimmedText, formats};
  },

  trim(text: string, formats: Format): {text: string; formats: Format} {
    // When trimming whitespace with adjacent internal link chip = split index lies after the chip
    // Set afterPoint flag to false in trimLeft and true in trimRight
    const trimmedLeft = this.trimLeft(text, formats);
    const trimmed = this.trimRight(trimmedLeft.text, trimmedLeft.formats);
    return {text: trimmed.text, formats: trimmed.formats};
  },

  removeRange,

  /**
   * Test if all text within the given `selection` has the same formatting.
   *
   * @param {BenchlingSelection?} selection
   * @param {Format} formats - e.g. {"bold": [{start: 0, end: 1}}, ...}
   * @param {string, keyof Format} format - e.g. "bold"
   * @returns {boolean}
   */
  isSelectionFormatted(selection: BenchlingSelection | null, formats: Format, format: keyof Format): boolean {
    if (!selection) {
      return false;
    }
    if (formats == null || !formats[format]) {
      return false;
    }
    return formats[format].some(
      (range) => isFormatPosition(range) && range.start <= selection.start && range.end >= selection.end
    );
  },

  getFormatShortcut(format: string): string | undefined {
    return FORMAT_TO_SHORTCUT[format];
  },
};

export default formatters;
